import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConnectionConfiguration, Destination, Member, Organization } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import SelectInput from "../common/SelectInput";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { checkSchema } from "../common/SchemaFormTemplates/checkSchema";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import TestAction from "../common/TestAction";

type DestinationDetailsModalConfigProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChange: Function,
    onChangeSelectedDestinationConfig: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onChangeSelectedDestinationOwner: Function,
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    onToggleModal: MouseEventHandler,
    errors: any,
    rsjfErrors: any,
}

const DestinationDetailsModalConfig = ({
    currentMemberIsAdmin,
    destination,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChange,
    onChangeSelectedDestinationConfig,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onChangeSelectedDestinationOwner,
    onSave,
    onToggleModal,
    errors,
    rsjfErrors,
}: DestinationDetailsModalConfigProps) => {
    const { t } = useTranslation();
    const [activeActionId, setActiveActionId] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const shouldHideUrlField = isHiddenUrlDestinationType(destination.type.name);

    return (
        <div className="h-full flex flex-col bg-very-light-gray dark:bg-off-black pb-16">
            <div className="flex flex-row w-full">
                <div className="w-2/3 rounded-lg py-8 px-4">
                    <div className="flex flex-col mb-2">
                        <label className="text-sm text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300">{t("Name")} *</label>
                        <input
                            id="name"
                            placeholder=""
                            className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 dark:bg-neutral-gray-700 dark:text-white transition-colors duration-300"
                            value={destination.name ? destination.name : ""}
                            onChange={(e) => onChange(e)}>
                        </input>
                        {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                    </div>
                    <div className="flex flex-col my-2">
                        {/* owner */}
                        <label className="text-sm text-secondary-gray dark:text-gray-lines">{t("Workspace")} *</label>
                        <SelectInput
                            options={organizations.map((org: Organization) => {
                                return {
                                    label: org.name,
                                    value: org.id
                                }
                            })}
                            className={"basic-single dark:text-white pb-1"}
                            value={{ label: destination.owner.name, value: destination.owner.id }}
                            autoSelectSingleOption={true}
                            isDisabled={false}
                            isLoading={false}
                            isClearable={false}
                            isSearchable={true}
                            isMulti={false}
                            name={t("Workspace")}
                            onChange={(e: any) => onChangeSelectedDestinationOwner(e)}
                            onMenuScrollToBottom={loadMoreOrgs}
                            handleInputChange={orgInputChange}
                        />
                        {errors.owner && <div className="text-orange-600 bg-orange-100 py-1 px-2 rounded"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                    </div>
                    {/* url */}
                    {!shouldHideUrlField && <div className="flex flex-col">
                        <label className="text-sm text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300">{t("URL")} *</label>
                        <input
                            id="base_url"
                            placeholder=""
                            className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 dark:bg-neutral-gray-700 dark:text-white transition-colors duration-300"
                            value={destination.base_url ? destination.base_url : ""}
                            onChange={(e) => onChange(e)}>
                        </input>
                        {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                    </div>}
                    <div className="flex-grow pt-14">
                        {/* <label>Configurations</label> */}
                        {destination.type.actions.filter((item: any) => item.type !== "pull").map((item: any) => {
                            const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                            const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                            return (
                                <div key={item.id} className={checkSchema(item) ? "pb-14" : "hidden"}>
                                    {checkSchema(item) && (
                                        <>
                                            <div className="mb-4 flex flex-col items-start w-full">
                                                {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true ? (
                                                    <TestAction
                                                        actionId={item.id}
                                                        actionValue={item.value}
                                                        onTestAction={(id: any, value: any) => {
                                                            onTestAction(id, value);
                                                            setActiveActionId(id);
                                                        }}
                                                        isLoading={testActionLoading && activeActionId === item.id}
                                                        response={activeActionId === item.id ? testActionResponse : null}
                                                        formTitle={formTitle}
                                                        uiSchema={uiSchema}
                                                    />
                                                ) : (
                                                    <div className="text-2xl font-normal text-secondary-gray dark:text-gray-300 transition-all duration-300">{formTitle}</div>
                                                )}
                                                {uiSchema['ui:description'] && (
                                                    <div className="form-root-description !mb-0 text-gray-600 dark:text-gray-300 mt-1 transition-colors duration-300">
                                                        {uiSchema['ui:description']}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rounded border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-off-black p-5 mb-8 transition-colors duration-300">
                                                <SchemaForm
                                                    userIsAdmin={currentMemberIsAdmin}
                                                    formTitle={''}
                                                    schema={item.schema}
                                                    ui_schema={{...uiSchema, 'ui:description': ''}}
                                                    formdata={destination.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === item.id) !== -1 ? destination.configurations.find((a: ConnectionConfiguration) => a.action.id === item.id).data : {}}
                                                    onChange={(e: any) => onChangeSelectedDestinationConfig(e, item.id)}
                                                    isEditable={true}
                                                    errors={rsjfErrors[item.id]}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {/* advanced configurations */}
                    {shouldHideUrlField && <div className="pb-14">
                        <div className="mt-6 mb text-lg font-semibold text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300" onClick={() => setShowAdvanced(!showAdvanced)}>
                            {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                        </div>
                        {showAdvanced &&
                            <div className="ml-8 flex flex-col">
                                <label className="text-sm text-secondary-gray dark:text-neutral-gray-400 transition-colors duration-300">{t("URL")}</label>
                                <input
                                    id="base_url"
                                    placeholder=""
                                    className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-secondary-gray rounded rounded-sm px-2 dark:bg-neutral-gray-700 dark:text-white transition-colors duration-300"
                                    value={destination.base_url ? destination.base_url : ""}
                                    onChange={(e) => onChange(e)}>
                                </input>
                                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                            </div>}
                    </div>}
                </div>
                <div className="border border-gray-lines dark:border-secondary-gray ml-4 my-6 py-4 px-5 w-1/3 h-fit bg-white dark:bg-off-black hidden md:block transition-colors duration-300">
                    <h3 className="text-off-black dark:text-white text-xl">{t('Learn more about {{technology}} connections', { technology: destination.type.name })}</h3>
                    <p className="text-off-black dark:text-white text-base">{t("These articles in the knowledge base may be of assistance to you:")}</p>
                    <ul className="list-disc pl-5 text-blue-basic dark:text-light-blue-300 text-s underline decoration-solid">
                        <li>
                            <a href="https://support.earthranger.com/connections/destinations" target="_blank" rel="noreferrer" className="text-blue-basic dark:text-light-blue-300">
                                {t("Setting up a Destination in Gundi")}
                            </a>
                        </li>
                        {destination.type.help_center_url && <li>
                            <a href={destination.type.help_center_url} target="_blank" rel="noreferrer" className="text-blue-basic dark:text-light-blue-300">
                                {destination.type.name} {t("Destination Set Up")}
                            </a>
                        </li>}
                    </ul>
                </div>
            </div>
            {/* submit and cancel button */}
            <div className="fixed bottom-0 right-0 flex flex-row w-full pr-5 py-3 pl-16 bg-white/10 dark:bg-dark-gray/50 backdrop-blur shadow-lg transition-colors duration-300">
                <button className="font-bold bg-dark-green hover:bg-status-green text-white font-bold rounded px-7 py-2 transition-colors duration-300"
                    type="submit"
                    onClick={(e: any) => onSave(e, "save")}>
                    {t("Save")}
                </button>
                <button className={"bg-light-green text-dark-green font-bold rounded px-7 py-2 mx-3"}
                    type="submit"
                    onClick={(e: any) => onSave(e, "saveAndClose")}>
                    {t("Save and Close")}
                </button>
                <button className="font-bold text-medium-gray dark:text-neutral-gray-400 rounded px-7 mx-2 transition-colors duration-300"
                    type="button"
                    onClick={onToggleModal}>
                    {t("Cancel")}
                </button>
            </div>
        </div>

    )
}

export default DestinationDetailsModalConfig;